import { StorageDriver, FileMetadata } from './StorageDriver';

/**
 * Storage driver that uses Electron's IPC to communicate with the main process
 * for native filesystem operations. This allows MapleOS to use the real filesystem
 * instead of IndexedDB when running in Electron.
 */
export class ElectronNodeFSDriver implements StorageDriver {
  private electronAPI: any;

  constructor() {
    // Access the electronAPI exposed by the preload script
    this.electronAPI = (window as any).electronAPI;
    
    if (!this.electronAPI) {
      throw new Error('ElectronNodeFSDriver requires Electron environment with preload script');
    }
    
    console.log('ElectronNodeFSDriver initialized - using native filesystem');
  }

  async readFile(path: string): Promise<string | null> {
    try {
      return await this.electronAPI.readFile(path);
    } catch (error) {
      console.error('Error reading file:', error);
      return null;
    }
  }

  async writeFile(path: string, content: string): Promise<void> {
    try {
      await this.electronAPI.writeFile(path, content);
    } catch (error) {
      console.error('Error writing file:', error);
      throw error;
    }
  }

  async writeFileWithMeta(path: string, content: string, modified?: Date): Promise<void> {
    try {
      await this.electronAPI.writeFileWithMeta(path, content, modified?.toISOString());
    } catch (error) {
      console.error('Error writing file with metadata:', error);
      throw error;
    }
  }

  async deleteFile(path: string): Promise<void> {
    try {
      await this.electronAPI.deleteFile(path);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  async listFiles(path: string): Promise<string[]> {
    try {
      return await this.electronAPI.listFiles(path);
    } catch (error) {
      console.error('Error listing files:', error);
      return [];
    }
  }

  async exists(path: string): Promise<boolean> {
    try {
      return await this.electronAPI.exists(path);
    } catch (error) {
      console.error('Error checking existence:', error);
      return false;
    }
  }

  async createDirectory(path: string): Promise<void> {
    try {
      await this.electronAPI.createDirectory(path);
    } catch (error) {
      console.error('Error creating directory:', error);
      throw error;
    }
  }

  async createDirectoryWithMeta(path: string, modified?: Date): Promise<void> {
    try {
      await this.electronAPI.createDirectoryWithMeta(path, modified?.toISOString());
    } catch (error) {
      console.error('Error creating directory with metadata:', error);
      throw error;
    }
  }

  // Settings management (stored as JSON files in .settings directory)
  async saveSettings(appId: string, settings: any): Promise<void> {
    try {
      await this.electronAPI.saveSettings(appId, settings);
    } catch (error) {
      console.error('Error saving settings:', error);
      throw error;
    }
  }

  async loadSettings(appId: string): Promise<any> {
    try {
      return await this.electronAPI.loadSettings(appId);
    } catch (error) {
      console.error('Error loading settings:', error);
      return null;
    }
  }

  // Desktop configuration
  async saveDesktopConfig(key: string, config: any): Promise<void> {
    try {
      await this.electronAPI.saveDesktopConfig(key, config);
    } catch (error) {
      console.error('Error saving desktop config:', error);
      throw error;
    }
  }

  async loadDesktopConfig(key: string): Promise<any> {
    try {
      return await this.electronAPI.loadDesktopConfig(key);
    } catch (error) {
      console.error('Error loading desktop config:', error);
      return null;
    }
  }

  // Get all files (for migration/debugging)
  async getAllFiles(): Promise<FileMetadata[]> {
    try {
      const files = await this.electronAPI.getAllFiles();
      // Convert date strings back to Date objects
      return files.map((file: any) => ({
        ...file,
        modified: new Date(file.modified)
      }));
    } catch (error) {
      console.error('Error getting all files:', error);
      return [];
    }
  }

  // Clear all data
  async clearAll(): Promise<void> {
    try {
      await this.electronAPI.clearAll();
    } catch (error) {
      console.error('Error clearing all data:', error);
      throw error;
    }
  }
}

